import { createContext, useContext, useMemo } from 'react';

import type { GetMlsListingDrawerQuery } from '@graphql/__generated__/graphql';

import {
  type WithEffectiveAddress,
  readEffectiveAddress,
} from './effectiveAddress';

export type MlsLot = NonNullable<GetMlsListingDrawerQuery['mls_lots'][0]>;
export type MlsBroker = NonNullable<MlsLot['broker']>;
export type MlsProperty = NonNullable<MlsLot['property']>;

// Inject user_images from profile_image_url for shared components (UserAvatar, UserChip)
// that expect user_images but can't access cross-tenant images due to RLS
export const withBrokerImage = <
  T extends {
    broker?:
      | { id?: string | null; profile_image_url?: string | null }
      | null
      | undefined;
  },
>(
  lot: T,
): T => {
  const imageUrl = lot.broker?.profile_image_url;
  const brokerId = lot.broker?.id;
  if (!lot.broker || !imageUrl || !brokerId) {
    return lot;
  }
  return {
    ...lot,
    broker: {
      ...lot.broker,
      user_images: [
        {
          id: `mls-${brokerId}`,
          is_primary: true,
          image: { id: `mls-${brokerId}`, url: imageUrl },
        },
      ],
    },
  };
};

// Projects the gated address onto `lot.property` — the only place those
// fields can come from now. See effectiveAddress.ts.
export const withEffectiveAddress = <
  T extends {
    effective_address?: unknown;
    property?: Record<string, unknown> | null;
  },
>(
  lot: T,
): WithEffectiveAddress<T> => {
  if (!lot.property) {
    return { ...lot, property: null } as WithEffectiveAddress<T>;
  }
  const effective = readEffectiveAddress(lot.effective_address);
  return {
    ...lot,
    property: {
      ...lot.property,
      route: effective.route,
      street_number: effective.street_number,
      locality: effective.locality,
      postcode: effective.postcode,
      state: effective.state,
      country_code: effective.country_code,
      lat: effective.lat,
      lng: effective.lng,
      approximate: effective.approximate,
    },
  };
};

export type MlsListingContextValue = {
  lot: MlsLot;
  hasAccess: boolean;
  hasPendingRequest: boolean;
  isOwner: boolean;
  mlsShareId?: string;
  refetch: () => void;
  // Quick action data
  myWebsitePortal?: {
    id: string;
    name?: string | null;
    type?: string | null;
  } | null;
  isPublishedOnMyWebsite: boolean;
  docTemplates: Array<{
    id: string;
    name?: string | null;
    active?: boolean | null;
    doc_templates_tenants?: Array<{
      tenant_id: string;
      is_default?: boolean | null;
    }> | null;
    doc_templates_teams?: Array<{
      team_id: string;
      is_default?: boolean | null;
    }> | null;
  }>;
};

// What consumers actually get from the hooks: the provider stores the raw
// query result, the hooks hand back a lot whose `property` carries the gated
// address. Kept distinct from MlsListingContextValue so a component cannot
// read the address off the provider value and bypass the projection.
export type GatedMlsListingContextValue = Omit<
  MlsListingContextValue,
  'lot'
> & {
  lot: WithEffectiveAddress<MlsLot>;
};

export const MlsListingContext = createContext<MlsListingContextValue | null>(
  null,
);

export const useMlsListing = () => {
  const context = useContext(MlsListingContext);
  return useMemo(() => {
    if (!context) {
      return null;
    }
    return {
      ...context,
      lot: withEffectiveAddress(withBrokerImage(context.lot)),
    };
  }, [context]);
};

// Strict consumer for components that only ever render inside MlsListingContext.Provider
// (every MLS-specific page under pages/mls/). Throws if misused.
export const useMlsListingStrict = (): GatedMlsListingContextValue => {
  const ctx = useMlsListing();
  if (ctx == null) {
    throw new Error(
      'useMlsListingStrict must be called inside <MlsListingContext.Provider>',
    );
  }
  return ctx;
};

export const useMlsAccessCheck = () => {
  const ctx = useMlsListing();
  return {
    isMlsMode: ctx != null,
    isOwner: ctx?.isOwner ?? false,
    hasAccess: ctx?.hasAccess ?? false,
    isRestricted: ctx != null && !ctx.isOwner && !ctx.hasAccess,
    mlsShareId: ctx?.mlsShareId,
  };
};
